// Single source of truth for user-facing labels of internal enum values.
//
// Rule: no component renders a raw backend enum (CP_candidate, needs_review,
// minor_gap, ...) directly — it goes through this module. Labels here match
// the backend's display vocabulary (classification.PROPERTY_STATUS_LABELS)
// so the server-rendered `property_status_display` and client labels never
// disagree.

import type { TagTone } from '../components/ui/Tag';

// ---- Property status (community / separate characterization) ----

export type PropertyBadgeVariant = 'community' | 'separate' | 'mixed' | 'unknown';

interface PropertyStatusMeta {
  /** Full label for detail views and dropdowns. */
  label: string;
  /** Compact label for dense table badges. */
  short: string;
  badge: PropertyBadgeVariant;
  /** Plain-English explanation, used as a tooltip/title. */
  tooltip: string;
}

export const PROPERTY_STATUS: Record<string, PropertyStatusMeta> = {
  CP_candidate: {
    label: 'Community Property',
    short: 'Community',
    badge: 'community',
    tooltip: 'Presumed community property — acquired during the marriage (Tex. Fam. Code §3.003).',
  },
  SP_candidate: {
    label: 'Separate Property',
    short: 'Separate',
    badge: 'separate',
    tooltip: 'Separate property — owned before marriage or received by gift or inheritance. Must be proven by clear and convincing evidence.',
  },
  SP_suspected: {
    label: 'Likely Separate',
    short: 'Likely Separate',
    badge: 'mixed',
    tooltip: 'Signals suggest separate property, but it has not been confirmed — attorney review recommended.',
  },
  Mixed: {
    label: 'Mixed',
    short: 'Mixed',
    badge: 'mixed',
    tooltip: 'Contains both community and separate components (e.g. separate asset with community contributions).',
  },
  Unknown: {
    label: 'Unclassified',
    short: 'Unclassified',
    badge: 'unknown',
    tooltip: 'Not yet characterized — treated as community by default (§3.003) until reviewed.',
  },
};

export function propertyStatusLabel(status: string | null | undefined, format: 'full' | 'short' = 'full'): string {
  if (!status) return 'Unclassified';
  const meta = PROPERTY_STATUS[status];
  if (!meta) return status;
  return format === 'short' ? meta.short : meta.label;
}

export function propertyStatusBadge(status: string | null | undefined): PropertyBadgeVariant {
  return PROPERTY_STATUS[status ?? '']?.badge ?? 'unknown';
}

export function propertyStatusTooltip(status: string | null | undefined): string {
  return PROPERTY_STATUS[status ?? '']?.tooltip ?? '';
}

// ---- Party (who produced the documents / who owns the account) ----

export type Party = 'client' | 'opposing' | 'third_party' | 'joint';

/** Map a canonical party value onto the Tag tone wheel. */
export const PARTY_TONE: Record<Party, TagTone> = {
  client: 'client',
  opposing: 'opposing',
  third_party: 'third',
  joint: 'joint',
};

export function partyLabel(
  party: Party | null | undefined,
  clientName?: string | null,
  opposingName?: string | null,
): string {
  if (!party) return 'Source unknown';
  switch (party) {
    case 'client':
      return clientName || 'Client';
    case 'opposing':
      return opposingName || 'Opposing party';
    case 'third_party':
      return 'Third party';
    case 'joint':
      return 'Joint';
  }
}

const BADGE_TO_TONE: Record<PropertyBadgeVariant, TagTone> = {
  community: 'community',
  separate: 'separate',
  mixed: 'mixed',
  unknown: 'unknown',
};

/** Tag tone for a property status. */
export function propertyStatusTone(status: string | null | undefined): TagTone {
  return BADGE_TO_TONE[propertyStatusBadge(status)];
}

/** Options for characterizing assets, liabilities, and accounts. */
export const CHARACTERIZATION_OPTIONS = (['CP_candidate', 'SP_candidate', 'Mixed', 'Unknown'] as const)
  .map((value) => ({ value, label: PROPERTY_STATUS[value].label }));

/** Options for overriding a transaction's classification. */
export const TXN_OVERRIDE_OPTIONS = (['CP_candidate', 'SP_candidate', 'SP_suspected', 'Unknown'] as const)
  .map((value) => ({ value, label: PROPERTY_STATUS[value].label }));

// ---- Processing job status ----

interface JobStatusMeta {
  label: string;
  variant: 'info' | 'warning' | 'danger' | 'success' | 'default';
}

export const JOB_STATUS: Record<string, JobStatusMeta> = {
  pending: { label: 'Queued', variant: 'info' },
  processing: { label: 'Processing', variant: 'info' },
  completed: { label: 'Completed', variant: 'success' },
  needs_review: { label: 'Needs Review', variant: 'warning' },
  failed: { label: 'Failed', variant: 'danger' },
};

export function jobStatusLabel(status: string | null | undefined): string {
  return JOB_STATUS[status ?? '']?.label ?? (status || 'Unknown');
}

export function jobStatusVariant(status: string | null | undefined): 'info' | 'warning' | 'danger' | 'success' {
  const v = JOB_STATUS[status ?? '']?.variant;
  return v && v !== 'default' ? v : 'info';
}

// ---- Statement reconciliation status ----
// The backend emits reconciled / minor_gap / unreconciled / not_applicable;
// 'gap' and 'close' appear only on legacy rows and are kept for safety.

interface ReconStatusMeta {
  label: string;
  variant: 'warning' | 'danger' | null; // null = show nothing (success is silent)
  tooltip: string;
}

export const RECONCILIATION_STATUS: Record<string, ReconStatusMeta> = {
  reconciled: {
    label: 'Reconciled',
    variant: null,
    tooltip: 'Transactions add up to the change in balance exactly.',
  },
  not_applicable: {
    label: 'Not applicable',
    variant: null,
    tooltip: 'This account type cannot be balance-checked this way (e.g. wallets, amortizing loans).',
  },
  minor_gap: {
    label: 'Small gap',
    variant: 'warning',
    tooltip: 'A small difference remains between the transactions and the balance change — usually rounding, fees, or timing.',
  },
  gap: {
    label: 'Small gap',
    variant: 'warning',
    tooltip: 'A small difference remains between the transactions and the balance change.',
  },
  close: {
    label: 'Small gap',
    variant: 'warning',
    tooltip: 'The balance nearly reconciles; a small variance remains.',
  },
  unreconciled: {
    label: 'Needs review',
    variant: 'danger',
    tooltip: 'The transactions do not add up to the change in balance — some may be missing or misread.',
  },
};

export function reconciliationBadge(status: string | null | undefined): { variant: 'warning' | 'danger'; label: string } | null {
  const meta = RECONCILIATION_STATUS[status ?? ''];
  if (!meta || meta.variant === null) return null;
  return { variant: meta.variant, label: meta.label };
}

// ---- Uploaded-document types (server/document_classifier.py DOC_TYPES) ----

interface DocTypeMeta {
  label: string;
  /** Heading used when the Documents tree groups by type. */
  group: string;
}

export const DOC_TYPES: Record<string, DocTypeMeta> = {
  bank_statement: { label: 'Bank statement', group: 'Bank & credit card statements' },
  credit_card_statement: { label: 'Credit card statement', group: 'Bank & credit card statements' },
  brokerage_retirement: { label: 'Brokerage / retirement', group: 'Brokerage & retirement' },
  tax_return: { label: 'Tax return', group: 'Tax returns' },
  pay_stub: { label: 'Pay stub', group: 'Pay stubs' },
  mortgage_statement: { label: 'Mortgage statement', group: 'Mortgage statements' },
  brokerage_retirement_statement: { label: 'Brokerage / retirement', group: 'Brokerage & retirement' },
  real_estate_valuation: { label: 'Real estate valuation', group: 'Valuations' },
  vehicle_valuation: { label: 'Vehicle valuation', group: 'Valuations' },
  legal_document: { label: 'Legal document', group: 'Legal documents' },
  discovery_cover: { label: 'Production cover', group: 'Other documents' },
  other_non_financial: { label: 'Non-financial', group: 'Other documents' },
  business_interest: { label: 'Business interest', group: 'Business interests' },
  unknown: { label: 'Unclassified', group: 'Other documents' },
};

export function docTypeLabel(docType: string | null | undefined): string {
  if (!docType) return 'Unclassified';
  return DOC_TYPES[docType]?.label ?? docType;
}

export function docTypeGroup(docType: string | null | undefined): string {
  if (!docType) return 'Other documents';
  return DOC_TYPES[docType]?.group ?? 'Other documents';
}

// ---- Running-balance (per-transaction) verification status ----

export const VERIFICATION_STATUS: Record<string, { label: string; variant: 'success' | 'danger' } | null> = {
  verified: { label: 'Running balance verified', variant: 'success' },
  broken: { label: 'Running balance mismatch', variant: 'danger' },
  // partial / unverifiable / not_applicable render no header badge — the
  // detail panel explains them in prose.
  partial: null,
  unverifiable: null,
  not_applicable: null,
};

export function verificationBadge(status: string | null | undefined): { label: string; variant: 'success' | 'danger' } | null {
  return VERIFICATION_STATUS[status ?? ''] ?? null;
}
